import { fromEvent } from 'rxjs';
import postUnsubscribePreferences from 'api/postUnsubscribePreferences';

/**
 * Retrieves the Segment anonymous ID of the current user.
 * Ad blockers or script blockers may interfere with this method, preventing `analytics` from working correctly.
 * @returns {string|null} - Segment anonymous ID UUID or null if retrieving the id is not possible
 */
function getSegmentAnonymousId() {
  if (!window.analytics) {
    return null;
  }

  if (typeof window.analytics.user !== 'function') {
    return null;
  }

  const analyticsUser = window.analytics.user();
  if (!analyticsUser) {
    return null;
  }
  return analyticsUser.anonymousId();
}

/**
 * Extracts form data from the unsubscribe form.
 * @param {HTMLFormElement} form - The unsubscribe form element
 * @returns {Object} Object containing:
 *   - email: {string|null} The email address entered in the form
 *   - confirmEmail: {string} Value of the hidden honeypot field for bot detection
 *   - unsubscribeAll: {boolean} Whether the unsubscribe-all checkbox is checked
 *   - anonymousId: {string|null} Segment anonymous ID of the user
 *   - [preferenceName]: {boolean} Email preference checkboxes (dynamic keys)
 */
function getFormData(form) {
  const data = {};

  // this is the honeypot input for bots
  const honeypotField = form.querySelector('[data-unsubscribe-form-confirm-email]');

  const emailInput = form.querySelector('[data-unsubscribe-form-email]');
  const allCheckbox = form.querySelector('[data-unsubscribe-form-unsubscribe-all]');
  const preferences = form.querySelectorAll('[data-unsubscribe-email-types] input[type="checkbox"]');
  preferences.forEach(checkBox => {
    data[checkBox.name] = checkBox.checked;
  });

  data.email = emailInput ? emailInput.value : null;
  data.confirmEmail = honeypotField ? honeypotField.value : '';
  data.unsubscribeAll = allCheckbox.checked;
  data.anonymousId = getSegmentAnonymousId();

  return data;
}

/**
 * Enables or disables the submit button based on form validation state.
 * @param {boolean} enabled - Whether the button should be disabled (true) or enabled (false)
 */
function disableButton(enabled) {
  const submitButton = document.querySelector("[data-unsubscribe-form-submit]");
  submitButton.disabled = enabled;
}

/**
 * Handles the change event for the "unsubscribe from all" checkbox.
 * When checked, disables and unchecks all email type checkboxes.
 * @param {Event} event - The checkbox change event
 */
function handleCheckboxChange(event) {
  const formAllCheckbox = event.target;
  const emailTypeSection = document.querySelector('[data-unsubscribe-email-types]');
  const checkboxes = emailTypeSection.querySelectorAll('input[type="checkbox"]');
  checkboxes.forEach(checkbox => {
    checkbox.checked = false;
    checkbox.disabled = formAllCheckbox.checked;
  });
}

/**
 * Sets up event listener for the "unsubscribe from all" checkbox.
 * Subscribes to change events using RxJS fromEvent.
 */
function unsubscribeFromAllSetup() {
  const checkbox = document.querySelector('[data-unsubscribe-form-unsubscribe-all]');
  if (checkbox) {
    fromEvent(checkbox, 'change').subscribe(handleCheckboxChange);
  }
}


/**
 * Displays confirmation message after successful unsubscribe operation.
 * @param {Object} response - Response object from the API containing message
 * @param {boolean} response.success - Whether the operation was successful
 * @param {string} response.message - Confirmation message to display
 *
 */
function showConfirmation(response) {
  const confirmationContainer = document.querySelector('[data-unsubscribe-confirmation]');
  confirmationContainer.textContent = response.message;
}

/**
 * Clears any existing confirmation message from the confirmation container.
 */
function clearConfirmation() {
  const confirmationContainer = document.querySelector('[data-unsubscribe-confirmation]');
  confirmationContainer.textContent = '';
}

/**
 * Handles form submission for unsubscribe preferences.
 * Prevents default submission, extracts form data, and posts to API.
 * @param {Event} event - The form submit event
 */
function handleFormSubmit(event) {
  event.preventDefault();
  disableButton(true);

  const form = event.target;
  const data = getFormData(form);

  clearConfirmation();
  postUnsubscribePreferences(data)
    .then(showConfirmation)
    .then(() => disableButton(false));
}

/**
 * Sets up event listener for form submission.
 * Subscribes to submit events using RxJS fromEvent.
 */
function submitFormSetup() {
  const form = document.querySelector('[data-unsubscribe-form]');
  fromEvent(form, 'submit').subscribe(handleFormSubmit);
}

/**
 * Initializes all interaction handlers for the unsubscribe page.
 * Sets up checkbox and form submission event handlers.
 */
export default function init () {
  unsubscribeFromAllSetup();
  submitFormSetup();
}
